import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { theme } from '../styles/GlobalStyles';

const ExperienceSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background: ${theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing.xxl};
  color: ${theme.colors.text};
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.secondary});
    transform: translateX(-50%);

    @media (max-width: ${theme.breakpoints.mobile}) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: ${theme.spacing.xxl};
  display: flex;
  align-items: center;

  &:nth-child(odd) {
    flex-direction: row;

    @media (max-width: ${theme.breakpoints.mobile}) {
      flex-direction: column;
      align-items: flex-start;
      margin-left: 40px;
    }
  }

  &:nth-child(even) {
    flex-direction: row-reverse;

    @media (max-width: ${theme.breakpoints.mobile}) {
      flex-direction: column;
      align-items: flex-start;
      margin-left: 40px;
    }
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 50%;
  top: 20px;
  width: 16px;
  height: 16px;
  background: ${theme.colors.primary};
  border: 4px solid ${theme.colors.background};
  border-radius: 50%;
  transform: translateX(-50%);
  z-index: 2;

  @media (max-width: ${theme.breakpoints.mobile}) {
    left: 20px;
  }
`;

const TimelineContent = styled.div`
  width: 45%;
  padding: ${theme.spacing.lg};
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    margin-top: ${theme.spacing.md};
  }
`;

const JobTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.3rem;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
`;

const Company = styled.div`
  color: ${theme.colors.text};
  font-size: 1.1rem;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 500;
`;

const Duration = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.md};
  font-weight: 500;
`;

const Description = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const Technologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

const TechTag = styled.span`
  background: rgba(37, 99, 235, 0.2);
  color: ${theme.colors.primary};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(37, 99, 235, 0.3);
`;

const Experience: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const experiences = [
    {
      title: 'Director of Sales and Marketing',
      company: 'Quilt',
      duration: '2022 - Present',
      description: 'Lead go-to-market strategy for metadata-driven Scientific Data Management Platform serving life sciences and biotech. Increased revenue from $1.2M to $2.4M ARR while securing partnerships with Biogen, Amgen, Flagship Pioneering, and Vertex Pharmaceuticals.',
      technologies: ['AWS Marketplace', 'SEO Strategy', 'Partnership Development', 'Scientific Data Management', 'Life Sciences', 'Revenue Growth']
    },
    {
      title: 'Startup Segment Leader',
      company: 'Innovative Solutions (AWS Premier Partner)',
      duration: 'March 2022 - August 2022',
      description: 'Led strategic initiative to establish North America\'s foremost AWS Startups Consulting Partner, serving companies from founder-stage through 300+ employee organizations ready for IPO.',
      technologies: ['AWS Partner Development', 'Startup Consulting', 'Business Development', 'Go-to-Market Strategy', 'Account Management']
    },
    {
      title: 'Director of Healthcare and Life Sciences',
      company: 'PrivoIT LLC (AWS Premier Partner)',
      duration: '2021 - 2022',
      description: 'Led life science industry account sales for customers from seed-stage through publicly traded companies. Achieved President\'s Club recognition and generated $1.8M in new revenue in 2021. Managed 30 accounts simultaneously.',
      technologies: ['AWS Solutions Architecture', 'Life Sciences', 'Cloud Adoption', 'Genomics', 'Sales Leadership', 'Webinar Series']
    },
    {
      title: 'Senior Account Executive',
      company: 'PrivoIT LLC',
      duration: '2018 - 2021',
      description: 'Territory sales for New England patch, working with inbound customers to engineer cloud solutions. Guided customers through cloud adoption, genomic sequencing pipelines, and data lake formation.',
      technologies: ['AWS', 'Cloud Migration', 'Genomics Pipelines', 'Data Lakes', 'Infrastructure as Code', 'High Performance Computing']
    },
    {
      title: 'Co-Founder, CTO',
      company: 'Cathexis Labs',
      duration: 'December 2016 - September 2017',
      description: 'Consumer food waste reduction technology startup. Designed and constructed hardware/software prototypes for first-of-its-kind consumer product detecting vegetable decay through off-gassing analysis.',
      technologies: ['Hardware Prototyping', 'Software Development', 'Product Development', 'Market Testing', 'Startup Operations']
    },
    {
      title: 'Technology Manager',
      company: 'MECCO Marking & Traceability',
      duration: '2013 - 2015',
      description: 'Conceived, developed, and patented new communication device grossing $1.3M (>10% of company revenue) in first year. Led redesign of flagship product improving profit margin from 33% to 52%.',
      technologies: ['Industrial Automation', 'Machine Vision', 'Robotics', 'PLC Programming', 'Product Development', 'Patent Development']
    }
  ];

  return (
    <ExperienceSection id="experience" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Experience
        </SectionTitle>

        <Timeline>
          {experiences.map((experience, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <TimelineDot />
              <TimelineContent
                as={motion.div}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <JobTitle>{experience.title}</JobTitle>
                <Company>{experience.company}</Company>
                <Duration>{experience.duration}</Duration>
                <Description>{experience.description}</Description>
                <Technologies>
                  {experience.technologies.map((tech, techIndex) => (
                    <TechTag key={techIndex}>{tech}</TechTag>
                  ))}
                </Technologies>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Container>
    </ExperienceSection>
  );
};

export default Experience;
